import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Banners } from '@sorare/core/src/components/layout/Banners';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/football';
import { breakpoints } from '@sorare/core/src/style/mediaQuery';

import { FirstTime } from '@sorare/marketplace/src/components/forYou/FirstTime';
import { Grid } from '@sorare/marketplace/src/components/forYou/Grid';
import { PromotionalEvents } from '@sorare/marketplace/src/components/forYou/PromotionalEvents';
import { ForYouRoot } from '@sorare/marketplace/src/components/forYou/ui';

import PlayerDetails from '@sorare/gameplay/src/components/player/PlayerDetails';

import {
  BaseballForYouQuery,
  BaseballForYouQueryVariables,
} from './__generated__/page.graphql';

const BASEBALL_FOR_YOU_QUERY = gql`
  query BaseballForYouQuery {
    forYou {
      featuredLeagues(sport: BASEBALL) {
        id
        displayName
        slug
      }
    }
    tokens {
      promotionalEvents(sport: BASEBALL) {
        slug
        ...PromotionalEvents_PromotionalEvent
      }
    }
  }
  ${PromotionalEvents.fragments.PromotionalEvent}
` as TypedDocumentNode<BaseballForYouQuery, BaseballForYouQueryVariables>;

export const ForYou = () => {
  const {
    flags: { usePromotionalEvents = false },
  } = useFeatureFlags();
  useTitleAndDescription(metadatas.market.title, metadatas.market.description);

  const { data, loading } = useQuery(BASEBALL_FOR_YOU_QUERY);

  const options = data?.forYou?.featuredLeagues.map(l => ({
    value: l.slug,
    label: l.displayName,
  }));
  const promotionalEvents = data?.tokens.promotionalEvents;
  const announcementBlock = useMemo(() => {
    if (usePromotionalEvents && promotionalEvents?.length)
      return <PromotionalEvents promotionalEvents={promotionalEvents} />;
    return <FirstTime />;
  }, [promotionalEvents, usePromotionalEvents]);

  if (loading || !options) {
    return <LoadingIndicator fullHeight />;
  }

  return (
    <ForYouRoot>
      <Container maxWidth={breakpoints.largeDesktop}>
        <Vertical gap={2}>
          <Banners />
          <Vertical gap={4}>
            {announcementBlock}
            <Grid
              options={options}
              sport={Sport.BASEBALL}
              PlayerDetails={PlayerDetails}
            />
          </Vertical>
        </Vertical>
      </Container>
    </ForYouRoot>
  );
};

export default ForYou;
